import { useEffect } from 'react'
import { useGetProfile } from 'api/generated/profile/profile'
import { clearIterableUser, setIterableUser } from 'lib/iterable/client'
import { selectIterableAnnouncementsEnabled } from 'store/flags/FlagsSelectors'
import { useSelector } from 'store/hooks/useSelector'

const selectLoggedIn = (state: Store) => state.auth.loggedIn

/**
 * Tells Iterable who the signed-in provider is, mirroring `SentryUserSync` / `useIdentifyUser`.
 *
 * Renders nothing; it exists so the SDK's identity follows the session without the dashboard
 * having to know about it.
 *
 * `ProviderId` is the provider's global key — the same value the backend signs into the Iterable
 * JWT's `userId` claim (QMD-4691), which is what makes the two sides agree on who this is.
 *
 * The profile query is gated on `loggedIn` for the reason spelled out in `SentryFeedbackWidget`:
 * this renders outside `<Routes>`, and an ungated `useGetProfile` fetches with no bearer token on
 * the login page, which has historically kicked off a refresh/logout cascade.
 *
 * Identity is gated on the announcements flag as well, because identifying is not free: the first
 * `setIterableUser` initialises the SDK and makes the backend mint an Iterable JWT. A kill switch
 * that left that running could not stop a failing token endpoint without a release, which is the
 * one thing it exists to do. Turning the flag off also clears the identity we already set.
 */
export function IterableIdentitySync() {
  const isEnabled = useSelector(selectIterableAnnouncementsEnabled)
  const loggedIn = useSelector(selectLoggedIn)
  const { data: profile } = useGetProfile({ query: { enabled: loggedIn } })
  const providerId = profile?.ProviderId

  useEffect(() => {
    if (isEnabled && loggedIn && providerId) {
      void setIterableUser(providerId)
      return
    }
    // Safe with the flag off and no client ever built: `clearIterableUser` reaches the SDK through
    // `client?.logout()`, so it never constructs one just to tear it down.
    clearIterableUser()
  }, [isEnabled, loggedIn, providerId])

  return null
}
